import { useEffect, useRef, useState } from "react";

import isEqual from "lodash/isEqual";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

export interface FilterOption<T = string | number | null> {
  value: T;
  label: string;
  selectedLabel?: string;
  showDivider?: boolean;
}

interface FilterContainerProps {
  children: React.ReactNode;
  isLoading: boolean;
  maxHeight: string;
  leftIcon: React.ReactNode;
  rightIcon: React.ReactNode;
  showDropdownIcon: boolean;
  getButtonLabel: () => string;
  size: "sm" | "lg";
  className: string;
  onClose?: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

// Rename FilterContainer to FilterBase since it's the base implementation
function FilterBase({
  children,
  isLoading,
  maxHeight,
  leftIcon,
  rightIcon,
  showDropdownIcon,
  getButtonLabel,
  size,
  className,
  onClose,
  isOpen,
  setIsOpen,
}: FilterContainerProps) {
  const [buttonWidth, setButtonWidth] = useState<number | undefined>();
  const buttonRef = useRef<HTMLDivElement>(null);

  // Measure button width for layout
  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      onClose?.();
    }
  };

  return (
    <DropdownMenu open={isOpen && !isLoading} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <div ref={buttonRef}>
          <div
            className={cn(
              "inline-flex items-center justify-center cursor-pointer rounded-full whitespace-nowrap",
              "border text-sm font-semibold transition-colors focus-visible:outline-none",
              "h-8 gap-1.5 bg-white px-4 py-1.5",
              size === "lg" ? "h-12 rounded-lg px-3.5 py-3" : "",
              isOpen
                ? "border-blue-500 text-blue-500"
                : "border-[#E2E2E2] text-gray-700 hover:bg-gray-50",
              className,
            )}
          >
            {leftIcon && leftIcon}
            {getButtonLabel()}
            {rightIcon ||
              (showDropdownIcon && (isOpen ? <ChevronUp /> : <ChevronDown />))}
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="filter-dropdown p-0 bg-white"
        align="start"
        side="bottom"
        avoidCollisions={false}
      >
        <div
          className="mt-2"
          style={{
            minWidth: buttonWidth ? `${buttonWidth}px` : "auto",
            maxHeight: maxHeight,
            overflowY: "auto",
          }}
        >
          {children}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface BaseFilterProps {
  label: string;
  selectedLabel?: string;
  className?: string;
  size?: "sm" | "lg";
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  showDropdownIcon?: boolean;
  maxHeight?: string;
  onClose?: () => void;
}

// MultiFilter for checkbox selection
interface MultiFilterProps<T> extends BaseFilterProps {
  values?: T[];
  options: readonly FilterOption<T>[];
  onChange?: (values: T[]) => void;
  showViewAll?: boolean;
  isLoading?: boolean;
  onSearchChange?: (value: string) => void;
  onViewAllChange?: (isViewAll: boolean) => void;
  showSearch?: boolean;
}

function MultiFilter<T>({
  values = [],
  label,
  selectedLabel,
  options,
  className,
  size = "sm",
  onChange,
  showViewAll = true,
  rightIcon,
  leftIcon,
  showDropdownIcon = true,
  showSearch = false,
  onSearchChange,
  onViewAllChange,
  onClose,
  maxHeight = "320px",
  isLoading = false,
}: MultiFilterProps<T>) {
  const [multipleValues, setMultipleValues] = useState<T[]>(values);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Sync internal state with external props
  useEffect(() => {
    if (!isEqual(values, multipleValues)) {
      setMultipleValues(values);
    }
  }, [values, multipleValues]);

  const handleCheckboxToggle = (value: T) => {
    const newValues = multipleValues.includes(value)
      ? multipleValues.filter((v) => v !== value)
      : [...multipleValues, value];

    setMultipleValues(newValues);
    onChange?.(newValues);
    onViewAllChange?.(newValues.length === options.length);
  };

  const handleViewAll = () => {
    if (multipleValues.length === options.length) {
      setMultipleValues([]);
      onChange?.([]);
      onViewAllChange?.(false);
    } else {
      const allValues = options.map((option) => option.value);
      setMultipleValues(allValues);
      onChange?.(allValues);
      onViewAllChange?.(true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  // Hide "View All" when there's an active search
  const shouldShowViewAll = showViewAll && !searchValue.trim();

  const getButtonLabel = () => {
    if (selectedLabel) {
      return selectedLabel;
    }

    const isAllSelected =
      multipleValues.length === options.length && !searchValue.trim();

    if (isAllSelected && showViewAll) {
      return label;
    } else if (multipleValues.length > 0) {
      return `${label} (${multipleValues.length})`;
    } else {
      return `${label} ${showViewAll ? "(None)" : ""}`;
    }
  };

  return (
    <FilterBase
      isLoading={isLoading}
      maxHeight={maxHeight}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      showDropdownIcon={showDropdownIcon}
      getButtonLabel={getButtonLabel}
      size={size}
      className={className || ""}
      onClose={onClose}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <>
        {shouldShowViewAll && (
          <div className="flex flex-col">
            <div
              className="flex cursor-pointer items-center gap-2 bg-white p-2"
              onClick={handleViewAll}
            >
              <Checkbox
                className="size-5"
                checked={multipleValues.length === options.length}
              />
              <Label className="cursor-pointer">View All</Label>
            </div>
            <div className="h-[1px] w-full bg-gray-200" />
          </div>
        )}

        {showSearch && (
          <div className="flex items-center gap-2 p-2">
            <Input
              placeholder="Search"
              className="w-full"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        )}
        {options.map((option) => (
          <div
            key={String(option.value)}
            className="flex cursor-pointer gap-2 p-2"
            onClick={() => handleCheckboxToggle(option.value)}
          >
            <Checkbox
              className="size-5"
              checked={multipleValues.includes(option.value)}
            />
            <Label className="max-w-[300px] cursor-pointer leading-[1.5]">
              {option.label}
            </Label>
          </div>
        ))}
      </>
    </FilterBase>
  );
}

// SingleFilter for radio button selection
interface SingleFilterProps<T> extends BaseFilterProps {
  value: T;
  options: readonly FilterOption<T>[];
  onChange: (value: T) => void;
}

function SingleFilter<T>({
  value,
  label,
  selectedLabel,
  options,
  className,
  size = "sm",
  onChange,
  rightIcon,
  leftIcon,
  showDropdownIcon = true,
  onClose,
  maxHeight = "320px",
}: SingleFilterProps<T>) {
  const [singleValue, setSingleValue] = useState<T>(value);
  const [isOpen, setIsOpen] = useState(false);

  // Sync internal state with external props
  useEffect(() => {
    if (value !== singleValue) {
      setSingleValue(value);
    }
  }, [value, singleValue]);

  const handleSelect = (optionValue: T) => {
    setSingleValue(optionValue);
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const getButtonLabel = () => {
    if (selectedLabel) {
      return selectedLabel;
    }

    const selectedOption = options.find(
      (option) => option.value === singleValue,
    );
    const newLabel = selectedOption?.selectedLabel || selectedOption?.label;
    return newLabel || label;
  };

  return (
    <FilterBase
      isLoading={false}
      maxHeight={maxHeight}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      showDropdownIcon={showDropdownIcon}
      getButtonLabel={getButtonLabel}
      size={size}
      className={className || ""}
      onClose={onClose}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <RadioGroup className="p-2">
        {options.map((option) => (
          <div
            key={String(option.value)}
            className="flex cursor-pointer items-center space-x-2"
            onClick={() => handleSelect(option.value)}
          >
            <RadioGroupItem
              checked={singleValue === option.value}
              value={String(option.value) || ""}
              id={String(option.value) || ""}
              className="size-5"
            />
            <Label
              htmlFor={String(option.value) || ""}
              className="cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </FilterBase>
  );
}

/**
 * Unified Filter component that delegates to SingleFilter or MultiFilter based on variant
 */

// Discriminated union types for the component props
type SingleFilterVariantProps<T> = SingleFilterProps<T> & {
  variant: "single";
  onChange: (value: T) => void;
};

type MultipleFilterVariantProps<T> = MultiFilterProps<T> & {
  variant: "multiple";
  onChange?: (values: T[]) => void;
};

type FilterProps<T> =
  | SingleFilterVariantProps<T>
  | MultipleFilterVariantProps<T>;

export function Filter<T>(props: FilterProps<T>) {
  const { variant, ...restProps } = props;

  if (variant === "single") {
    const { onChange, ...singleProps } = restProps as Omit<
      SingleFilterProps<T>,
      "variant"
    >;
    return <SingleFilter {...singleProps} onChange={onChange} />;
  }

  if (variant === "multiple") {
    const { onChange, ...multiProps } = restProps as Omit<
      MultiFilterProps<T>,
      "variant"
    >;
    return <MultiFilter {...multiProps} onChange={onChange} />;
  }

  return null;
}
